import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Badge, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExchangeAlt, faMoneyBillWave, faCreditCard,
    faMoneyCheckAlt, faUniversity, faQuestionCircle,
    faArrowDown, faArrowUp
} from '@fortawesome/free-solid-svg-icons';

const FacturaCard = ({ factura, facturasSeleccionadas, toggleSeleccionFactura, onConciliar, movimientoSeleccionado, cargandoConciliacion }) => {

    // Función para obtener el icono según la forma de pago
    const getFormaPagoIcon = (formaPagoStr) => {
        if (!formaPagoStr) return faQuestionCircle;

        const clave = formaPagoStr.split('-')[0].trim();

        switch (clave) {
            case '01': // Efectivo
                return faMoneyBillWave;
            case '02': // Cheque nominativo
                return faMoneyCheckAlt;
            case '03': // Transferencia electrónica de fondos
                return faExchangeAlt;
            case '04': // Tarjeta de crédito
            case '28': // Tarjeta de débito
                return faCreditCard;
            case '99': // Por definir
                return faQuestionCircle;
            default:
                return faUniversity;
        }
    };

    const getTipoComprobanteConfig = (tipo) => {
        const configs = {
            'I': { label: 'I - Ingreso', variant: 'success', icon: faArrowDown },
            'E': { label: 'E - Egreso', variant: 'danger', icon: faArrowUp },
            'P': { label: 'P - Pago', variant: 'info', icon: faMoneyBillWave }
        };
        // faArrowDown y faArrowUp we'll import them locally or just use strings if we didn't import them.
        // Wait, they were imported in ConciliarFacturas.js. We need to import them here.
        return configs[tipo] || { label: 'Desconocido', variant: 'secondary', icon: faMoneyBillWave };
    };

    const tipoConfig = getTipoComprobanteConfig(factura.tipo);
    const isFechaMatch = movimientoSeleccionado && factura.fecha && movimientoSeleccionado.fecha &&
        factura.fecha.substring(0, 10) === movimientoSeleccionado.fecha.substring(0, 10);

    return (
        <>
            <style>
                {`
                @keyframes matchBlink {
                    0% { box-shadow: 0 0 5px rgba(255, 193, 7, 0.3); border-color: rgba(255, 193, 7, 0.5); }
                    50% { box-shadow: 0 0 15px rgba(255, 193, 7, 0.8); border-color: rgba(255, 193, 7, 1); }
                    100% { box-shadow: 0 0 5px rgba(255, 193, 7, 0.3); border-color: rgba(255, 193, 7, 0.5); }
                }
                .card-match-blink {
                    animation: matchBlink 2s infinite;
                    border: 2px solid #ffc107 !important;
                }
            `}
            </style>
            <Card
                className={`shadow-sm hover-shadow transition-base ${facturasSeleccionadas.some(f => f.id === factura.id) ? 'border-primary border-2 bg-soft-primary' : isFechaMatch ? 'card-match-blink' : factura.montoConciliado > 0 ? 'border-warning border-2 bg-soft-warning' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleSeleccionFactura(factura)}
            >
                <Card.Body className="p-3">
                    <Row className="align-items-center mb-2">
                        <Col>
                            <div className="d-flex align-items-center">
                                {facturasSeleccionadas.some(f => f.id === factura.id) && (
                                    <i className="fas fa-check-circle text-primary me-2 fs-3"></i>
                                )}
                                <div
                                    className="bg-soft-info rounded p-2 me-2"
                                    style={{ width: '40px', height: '40px' }}
                                >
                                    <i className="fas fa-file-invoice text-info fs-1"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 text-800">
                                        {factura.serie ? `${factura.serie}-` : ''}{factura.folio}
                                    </h6>
                                    <div className="mt-1 d-flex gap-2 flex-wrap align-items-center">
                                        <Badge bg={isFechaMatch ? "warning" : "soft-info"} text={isFechaMatch ? "dark" : "info"} className={`fs--2 d-flex align-items-center gap-1 d-inline-flex border ${isFechaMatch ? 'border-warning' : 'border-info-subtle'} py-1 px-2`}>
                                            <i className="far fa-calendar"></i>
                                            {(() => {
                                                const d = new Date(factura.fecha);
                                                const day = d.getDate().toString().padStart(2, '0');
                                                const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                                                const month = months[d.getMonth()];
                                                return `${day}/${month}/${d.getFullYear()}`;
                                            })()}
                                        </Badge>
                                        {isFechaMatch && (
                                            <Badge bg="warning" text="dark" className="fs--2 d-flex align-items-center gap-1 py-1 px-2">
                                                <i className="fas fa-star text-danger"></i> ¡Coincide fecha!
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col xs="auto" className="d-flex flex-column align-items-end gap-1">
                            <Badge bg={tipoConfig.variant} className="d-flex align-items-center gap-1 fs--2">
                                <i className={`fas ${factura.tipo === 'I' ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
                                {tipoConfig.label}
                            </Badge>
                            <Badge
                                bg={factura.metodoPago === 'PPD' ? 'danger' : 'light'}
                                text={factura.metodoPago === 'PPD' ? 'white' : 'dark'}
                                className={`d-flex align-items-center justify-content-center gap-1 fs--2 ${factura.metodoPago !== 'PPD' ? 'border' : ''}`}
                            >
                                {factura.metodoPago || 'N/A'}
                            </Badge>
                            {factura.montoConciliado > 0 && (
                                <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 fs--2 mt-1">
                                    <i className="fas fa-exclamation-triangle"></i> Conciliación Parcial
                                </Badge>
                            )}
                        </Col>
                    </Row>

                    <div className="mb-2">
                        <div className="text-700 fw-semi-bold fs--1 mb-1 text-truncate d-flex align-items-center">
                            {factura.rfc !== 'XEXX010101000' && (
                                <img src="/img/bandera-mexico-48.png" width="16" alt="México" className="me-2" />
                            )}
                            <i className="fas fa-building me-2 text-600"></i>
                            <span className="text-truncate">{factura.rfc} - {factura.receptor}</span>
                            {factura.rfc === 'XEXX010101000' && (
                                <i className="fas fa-globe-americas text-info ms-2" title="Extranjero"></i>
                            )}
                        </div>
                        {factura.formaPago && (
                            <div className="text-600 fs--2 mt-1">
                                <FontAwesomeIcon icon={getFormaPagoIcon(factura.formaPago)} className="me-2" />
                                {factura.formaPago}
                            </div>
                        )}
                    </div>

                    <div className="border-top pt-2">
                        <Row className="align-items-center">
                            <Col>
                                <div className="text-600 fs--2 mb-1">Total</div>
                                <div className={`fw-bold fs-1 ${factura.origen === 'R' ? 'text-danger' : 'text-primary'}`}>
                                    ${factura.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                    <span className="text-600 fs--1 ms-2">{factura.moneda}</span>
                                </div>
                                {factura.moneda && factura.moneda !== 'MXN' && (
                                    <div className="text-600 fs--2">
                                        T.C. ${(Number(factura.tipoCambio) || 1).toFixed(4)} =
                                        <span className={`fw-semi-bold ms-1 ${factura.origen === 'R' ? 'text-danger' : 'text-800'}`}>
                                            ${(factura.totalConvertido !== undefined ? Number(factura.totalConvertido) : Number((factura.total * (Number(factura.tipoCambio) || 1)).toFixed(2))).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                )}
                            </Col>
                            <Col xs="auto" className="d-flex gap-2">
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="rounded-pill"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const tipoMov = movimientoSeleccionado?.tipo;
                                        let baseUrl = '';
                                        if (factura.tipo === 'P') {
                                            baseUrl = tipoMov === 'E'
                                                ? '/contabilidad/centro-xml/pagos-recibidos/visualizacion/'
                                                : '/contabilidad/centro-xml/pagos-emitidos/visualizacion/';
                                        } else {
                                            baseUrl = tipoMov === 'E'
                                                ? '/contabilidad/centro-xml/facturas-recibidas/visualizacion/'
                                                : '/contabilidad/centro-xml/facturas-emitidas/visualizacion/';
                                        }
                                        window.open(`${baseUrl}${factura.id}`, '_blank', 'noopener,noreferrer');
                                    }}
                                >
                                    <i className="fas fa-eye me-1"></i>
                                    Ver
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="rounded-pill"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onConciliar) onConciliar(factura);
                                    }}
                                    disabled={cargandoConciliacion}
                                >
                                    {cargandoConciliacion ? (
                                        <><Spinner animation="border" size="sm" className="me-1" /> ...</>
                                    ) : (
                                        <><i className="fas fa-link me-1"></i> Conciliar</>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};



FacturaCard.propTypes = {
    factura: PropTypes.object,
    facturasSeleccionadas: PropTypes.array,
    toggleSeleccionFactura: PropTypes.func,
    onConciliar: PropTypes.func,
    movimientoSeleccionado: PropTypes.object
};

export default FacturaCard;
